import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
  RadioGroup,
  Radio,
  FormControlLabel,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  FlashOn as ImmediateIcon,
  Bookmark as BookmarkIcon
} from '@mui/icons-material';
import { APIProvider } from '@vis.gl/react-google-maps';
import { styled } from '@mui/material/styles';
import { createGlobalStyle } from 'styled-components';
import { createFilterOptions } from '@mui/material/Autocomplete';
import PlaceAutocomplete from '../common/PlaceAutocomplete';

import {
  fetchRideTemplates,
  saveRideTemplate,
  updateRideTemplate
} from '../../redux/slices/ridesSlice';
import { fetchCustomers } from '../../redux/slices/customersSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

// Styled components
const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

// Globale Styles für Google Maps Autocomplete
const GlobalStyle = createGlobalStyle`
  .pac-container {
    z-index: 1500 !important;
    border-radius: 4px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    position: fixed !important;
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: white;
    
    @media (max-width: 768px) {
      max-height: 150px;
      font-size: 14px;
    }
    
    .pac-item {
      padding: 12px 16px;
      cursor: pointer;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
      transition: background-color 0.2s ease;
      line-height: 1.4;
      
      &:last-child {
        border-bottom: none;
      }
      
      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }
      
      &.pac-item-selected {
        background-color: rgba(25, 118, 210, 0.08);
      }
      
      @media (max-width: 768px) {
        padding: 10px 12px;
        min-height: 44px;
      }
    }
    
    .pac-icon {
      display: none !important;
    }
    
    .pac-item-query {
      font-size: 14px;
      color: rgba(0, 0, 0, 0.87);
      
      @media (max-width: 768px) {
        font-size: 13px;
      }
    }
    
    .pac-matched {
      font-weight: 600;
      color: #1976d2;
    }
    
    .pac-item-context {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.6);
      margin-top: 2px;
      
      @media (max-width: 768px) {
        font-size: 11px;
      }
    }
  }
  
  @media (max-width: 768px) {
    .pac-container {
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }
  }
`;


const TemplateForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const isEditMode = !!id;
  
  const { templates, isLoading: templatesLoading, error: templatesError } = useSelector(state => state.rides);
  const { customers, isLoading: customersLoading } = useSelector(state => state.customers);
  
  
  const [formData, setFormData] = useState({
    template_name: '',
    customer_name: '',
    customer_phone: '',
    customer_id: '',
    pickup_address: '',
    pickup_location: { latitude: null, longitude: null },
    destination_address: '',
    destination_location: { latitude: null, longitude: null },
    order_type: '2', // Default: Sofortbestellung
    vehicle_type: '1', // Default: PKW
  });
  
  const [formErrors, setFormErrors] = useState({
    template_name: '',
    customer_name: '',
    pickup_address: '',
    destination_address: '',
  });
  
  const [createNewCustomer, setCreateNewCustomer] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Load data
  useEffect(() => {
    dispatch(fetchCustomers());
    
    if (isEditMode) {
      dispatch(fetchRideTemplates());
    }
  }, [dispatch, isEditMode]);
  
  // Set form data from template when editing
  useEffect(() => {
    if (isEditMode && templates.length > 0) {
      const template = templates.find(t => t.id === id);
      
      if (template) {
        setFormData({
          template_name: template.template_name || '',
          customer_name: template.customer_name || '',
          customer_phone: template.customer_phone || '',
          customer_id: template.customer_id || '',
          pickup_address: template.pickup_address || '',
          pickup_location: template.pickup_location || { latitude: null, longitude: null },
          destination_address: template.destination_address || '',
          destination_location: template.destination_location || { latitude: null, longitude: null },
          order_type: template.order_type?.toString() || '2',
          vehicle_type: template.vehicle_type?.toString() || '1',
        });
      }
    }
  }, [isEditMode, id, templates]);
  
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Reset errors when field changes
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  const handleOrderTypeChange = (e) => {
    setFormData({
      ...formData,
      order_type: e.target.value
    });
  };
  
  // Filter-Option für Autocomplete
  const filter = createFilterOptions();
  
  const handleCustomerChange = (event, newValue) => {
    if (typeof newValue === 'string') {
      // Wenn eine Texteingabe gemacht wurde (kein Kunde aus der Liste ausgewählt)
      setFormData({
        ...formData,
        customer_id: '',
        customer_name: newValue || ''
      });
      setCreateNewCustomer(true);
    } else if (newValue && newValue.inputValue) {
      // Wenn "Neuen Kunden erstellen" ausgewählt wurde
      setFormData({
        ...formData,
        customer_id: '',
        customer_name: newValue.inputValue || ''
      });
      setCreateNewCustomer(true);
    } else {
      // Wenn ein existierender Kunde ausgewählt wurde
      setFormData({
        ...formData,
        customer_id: newValue?.id || '',
        customer_name: newValue?.name || newValue?.user?.name || '',
        customer_phone: newValue?.phone || newValue?.user?.phoneNumber || ''
      });
      setCreateNewCustomer(false);
    }
    
    if (formErrors.customer_name) {
      setFormErrors({
        ...formErrors,
        customer_name: ''
      });
    }
  };
  
  const handleCustomerNameChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      customer_name: value
    });
    
    if (formErrors.customer_name) {
      setFormErrors({
        ...formErrors,
        customer_name: ''
      });
    }
  };
  
  const handleCustomerPhoneChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      customer_phone: value
    });
  };
  
  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };
    
    if (!formData.customer_name) {
      newErrors.customer_name = 'Bitte geben Sie einen Kundennamen ein';
      valid = false;
    }
    
    if (!formData.pickup_address) {
      newErrors.pickup_address = 'Abholadresse ist erforderlich';
      valid = false;
    }
    
    if (!formData.destination_address) {
      newErrors.destination_address = 'Zieladresse ist erforderlich';
      valid = false;
    }
    
    setFormErrors(newErrors);
    return valid;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      if (isEditMode) {
        // Vorlage aktualisieren
        dispatch(updateRideTemplate({
          id,
          ...formData
        }))
          .then((result) => {
            if (!result.error) {
              setSnackbar({
                open: true,
                message: 'Vorlage erfolgreich aktualisiert',
                severity: 'success'
              });
              
              // Kurze Verzögerung, damit der Benutzer die Erfolgsmeldung sieht
              setTimeout(() => {
                navigate('/vorlagen');
              }, 1500);
            } else {
              setSnackbar({
                open: true,
                message: `Fehler beim Aktualisieren der Vorlage: ${result.error.message}`,
                severity: 'error'
              });
            }
          });
      } else {
        // Neue Vorlage speichern
        dispatch(saveRideTemplate(formData))
          .then((result) => {
            if (!result.error) {
              setSnackbar({
                open: true,
                message: 'Vorlage erfolgreich gespeichert',
                severity: 'success'
              });
              
              // Kurze Verzögerung, damit der Benutzer die Erfolgsmeldung sieht
              setTimeout(() => {
                navigate('/vorlagen');
              }, 1500);
            } else {
              setSnackbar({
                open: true,
                message: `Fehler beim Speichern der Vorlage: ${result.error.message}`,
                severity: 'error'
              });
            }
          });
      }
    }
  };
  
  const handleCancel = () => {
    navigate('/vorlagen');
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Loading state
  if ((isEditMode && templatesLoading) || customersLoading) {
    return <Loading message="Daten werden geladen..." />;
  }
  
  // Error state
  if (isEditMode && templatesError) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Vorlagendaten"
        message={templatesError}
        onRetry={() => dispatch(fetchRideTemplates())}
      />
    );
  }
  
  // Customer options
  const customerOptions = customers;
  
  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <GlobalStyle />
      <Box sx={{ flexGrow: 1, p: 3, overflow: 'visible' }}>
        <PageHeader
        title={isEditMode ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrten', path: '/fahrten' },
          { label: 'Vorlagen', path: '/vorlagen' },
          { label: isEditMode ? 'Bearbeiten' : 'Neu', path: isEditMode ? `/vorlagen/${id}/bearbeiten` : '/vorlagen/neu' }
        ]}
      />
      
      <form onSubmit={handleSubmit}>
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Vorlagendetails
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={12}>
              <TextField
                fullWidth
                id="template_name"
                name="template_name"
                label="Vorlagenname (optional)"
                value={formData.template_name}
                onChange={handleChange}
                placeholder="z.B. Standardfahrt zum Büro"
                helperText="Falls leer, wird automatisch aus Start- und Zieladresse generiert"
                InputProps={{
                  startAdornment: <BookmarkIcon color="primary" sx={{ mr: 1 }} />
                }}
              />
            </Grid>
          </Grid>
        </FormPaper>
        
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Kundeninformation
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                id="customer-select"
                options={customerOptions}
                sx={{ width: '100%' }}
                getOptionLabel={(option) => {
                  // Wenn ein String übergeben wird (direktes Tippen)
                  if (typeof option === 'string') {
                    return option;
                  }
                  // Wenn eine "Neuen Kunden erstellen" Option ist
                  if (option.inputValue) {
                    return option.inputValue;
                  }
                  // Normale Kundenoption
                  return option.name || option.user?.name || '';
                }}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                freeSolo
                value={createNewCustomer ? { inputValue: formData.customer_name } : customerOptions.find(c => c.id === formData.customer_id) || null}
                onChange={handleCustomerChange}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);
                  
                  const { inputValue } = params;
                  // Suggest the creation of a new value
                  const isExisting = options.some((option) => inputValue === option.name || inputValue === option.user?.name);
                  
                  if (inputValue !== '' && !isExisting) {
                    filtered.push({
                      inputValue,
                      name: `"${inputValue}" als neuen Telefonkunden anlegen`
                    });
                  }
                  
                  return filtered;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Kunde"
                    required
                    error={!!formErrors.customer_name}
                    helperText={formErrors.customer_name}
                    sx={{ minWidth: '350px', width: '100%' }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <PersonIcon color="primary" sx={{ mr: 1 }} />
                          {params.InputProps.startAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  // Wenn es die "Neuen Kunden erstellen" Option ist
                  if (option.inputValue) {
                    return (
                      <li {...props}>
                        <Typography variant="body1" color="primary">
                          {option.name} (als Telefonkunde)
                        </Typography>
                      </li>
                    );
                  }
                  
                  // Normale Kundenoption
                  return (
                    <li {...props}>
                      <Box>
                        <Typography variant="body1">{option.name || option.user?.name}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {option.email || option.user?.email} | {option.phone || option.user?.phoneNumber}
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
              />
            </Grid>
            
            {createNewCustomer && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  id="customer_phone"
                  name="customer_phone"
                  label="Telefonnummer"
                  value={formData.customer_phone}
                  onChange={handleCustomerPhoneChange}
                  placeholder="+49123456789"
                />
              </Grid>
            )}
          </Grid>
        </FormPaper>
        
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Bestelltyp
          </Typography>
          
          <FormControl component="fieldset">
            <RadioGroup
              row
              name="order_type"
              value={formData.order_type}
              onChange={handleOrderTypeChange}
            >
              <FormControlLabel 
                value="2" 
                control={<Radio />} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ImmediateIcon sx={{ mr: 1 }} />
                    <Box>
                      <Typography variant="body1">Sofortbestellung</Typography>
                      <Typography variant="body2" color="text.secondary">Fahrt beginnt sofort nach Fahrerzuweisung</Typography>
                    </Box>
                  </Box>
                } 
              />
              <FormControlLabel 
                value="1" 
                control={<Radio />} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ScheduleIcon sx={{ mr: 1 }} />
                    <Box>
                      <Typography variant="body1">Vorbestellung</Typography>
                      <Typography variant="body2" color="text.secondary">Fahrt für einen späteren Zeitpunkt planen</Typography>
                    </Box>
                  </Box>
                } 
              />
            </RadioGroup>
          </FormControl>
        </FormPaper>
        
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Fahrtinformationen
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={12}>
              <PlaceAutocomplete
                required
                fullWidth
                label="Abholadresse"
                value={formData.pickup_address}
                onChange={(e) => setFormData(prev => ({ ...prev, pickup_address: e.target.value }))}
                onPlaceSelect={(address, location) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    pickup_address: address,
                    pickup_location: location
                  }));
                }}
                error={!!formErrors.pickup_address}
                helperText={formErrors.pickup_address}
                placeholder="Straße, Hausnummer, PLZ, Ort"
              />
            </Grid>
            
            <Grid size={12}>
              <PlaceAutocomplete
                required
                fullWidth
                label="Zieladresse"
                value={formData.destination_address}
                onChange={(e) => setFormData(prev => ({ ...prev, destination_address: e.target.value }))}
                onPlaceSelect={(address, location) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    destination_address: address,
                    destination_location: location
                  }));
                }}
                error={!!formErrors.destination_address}
                helperText={formErrors.destination_address}
                placeholder="Straße, Hausnummer, PLZ, Ort"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="vehicle-type-label">Fahrzeugtyp</InputLabel>
                <Select
                  labelId="vehicle-type-label"
                  id="vehicle_type"
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleChange}
                  label="Fahrzeugtyp"
                >
                  <MenuItem value="1">PKW (5 Sitzplätze)</MenuItem>
                  <MenuItem value="2">Kombi (7 Sitzplätze)</MenuItem>
                  <MenuItem value="3">Bus (9 Sitzplätze)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </FormPaper>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button 
            variant="outlined" 
            onClick={handleCancel}
            startIcon={<CancelIcon />}
          >
            Abbrechen
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
          >
            {isEditMode ? 'Änderungen speichern' : 'Vorlage erstellen'}
          </Button>
        </Box>
      </form>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </APIProvider>
  );
};

export default TemplateForm;